import React, { useState, useEffect, useCallback } from 'react';
import { ManagedUser, UserFiltersState, UserListPagination, AccountStatus } from '../types';
import { UserManagementService } from '../services/UserManagementService';
import { UserFilters } from './UserFilters';
import { UserDetail } from './UserDetail';
import { UserExport } from './UserExport';
import './UserManagement.css';

const DEFAULT_FILTERS: UserFiltersState = {
  search: '',
  authMethod: 'all',
  status: 'all',
  sortBy: 'createdAt',
  sortOrder: 'desc',
  pageSize: 25,
};

export const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [pagination, setPagination] = useState<UserListPagination>({
    page: 1,
    pageSize: 25,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
  });
  const [filters, setFilters] = useState<UserFiltersState>(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const service = UserManagementService.getInstance();

  const loadUsers = useCallback(async (page: number = 1) => {
    setLoading(true);
    setError(null);

    try {
      const result = await service.listUsers(filters, page, filters.pageSize);
      setUsers(result.users);
      setPagination(result.pagination);
      setCurrentPage(page);
    } catch (err: any) {
      if (err.message === 'UNAUTHORIZED') {
        setError('Session expired. Please log in again.');
      } else {
        setError('Failed to load users. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [filters, service]);

  useEffect(() => {
    loadUsers(1);
  }, [filters]);

  const handleFiltersChange = useCallback((newFilters: UserFiltersState) => {
    setFilters(newFilters);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    loadUsers(page);
  }, [loadUsers]);

  const handleUserClick = useCallback((userId: string) => {
    setSelectedUserId(userId);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedUserId(null);
  }, []);

  const handleStatusChanged = useCallback((userId: string, newStatus: AccountStatus) => {
    setUsers(prev => prev.map(u =>
      u.id === userId ? { ...u, status: newStatus } : u
    ));
  }, []);

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="user-management">
      <div className="user-management__header">
        <h2 className="user-management__title">User Management</h2>
        <UserExport filters={filters} />
      </div>

      <UserFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        totalCount={pagination.totalCount}
        filteredCount={pagination.totalCount}
      />

      {error && (
        <div className="user-management__error">
          <span>{error}</span>
          <button onClick={() => loadUsers(currentPage)}>Retry</button>
        </div>
      )}

      <div className="user-management__table-container">
        {loading && <div className="user-management__loading-overlay">Loading...</div>}

        <table className="user-management__table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Method</th>
              <th>Registered</th>
              <th>Last Active</th>
              <th>Games</th>
              <th>Win Rate</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && !loading ? (
              <tr>
                <td colSpan={8} className="user-management__empty">
                  No users found
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr
                  key={user.id}
                  className="user-management__row"
                  onClick={() => handleUserClick(user.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleUserClick(user.id)}
                >
                  <td className="user-management__cell-name">{user.name}</td>
                  <td className="user-management__cell-email">{user.email || 'N/A'}</td>
                  <td>
                    <span className="user-management__auth-badge">{user.authMethod}</span>
                  </td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td>{formatDate(user.lastActiveAt)}</td>
                  <td>{user.gamesPlayed}</td>
                  <td>{Math.round(user.winRate * 100)}%</td>
                  <td>
                    <span className={`user-management__status-badge user-management__status-badge--${user.status}`}>
                      {user.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {pagination.totalPages > 1 && (
        <div className="user-management__pagination">
          <button
            className="user-management__page-btn"
            onClick={() => handlePageChange(1)}
            disabled={!pagination.hasPrevious}
            aria-label="First page"
          >
            ⟨⟨
          </button>
          <button
            className="user-management__page-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={!pagination.hasPrevious}
            aria-label="Previous page"
          >
            ⟨
          </button>
          <span className="user-management__page-info">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            className="user-management__page-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={!pagination.hasNext}
            aria-label="Next page"
          >
            ⟩
          </button>
          <button
            className="user-management__page-btn"
            onClick={() => handlePageChange(pagination.totalPages)}
            disabled={!pagination.hasNext}
            aria-label="Last page"
          >
            ⟩⟩
          </button>
        </div>
      )}

      {/* User Detail Panel */}
      {selectedUserId && (
        <UserDetail
          userId={selectedUserId}
          onClose={handleCloseDetail}
          onStatusChanged={handleStatusChanged}
        />
      )}
    </div>
  );
};
